// Copyright (c) 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import * as Sentry from '@sentry/browser'

import {
  SvgIcon,
  Table,
  Button,
  Warning,
  Select,
  Spinner
} from '../'

import { ReadOnlyContext } from '../../context'

import { createArrayWithSequence } from '../../utils'


export class CalculationTermComparison extends React.PureComponent {
  static propTypes = {
    overrideState: PropTypes.string.isRequired,
    overrideTerm: PropTypes.string,
    calcTerm: PropTypes.string,
    creditMaxTerm: PropTypes.number.isRequired,
    showOverride: PropTypes.bool.isRequired,
    overridePerformed: PropTypes.bool.isRequired,
    overrideShortest: PropTypes.object.isRequired,
    overrideLongest: PropTypes.object.isRequired,
    overrideActualWithTerm: PropTypes.object.isRequired,
    overrideCompleted: PropTypes.bool.isRequired,
    overrideRequested: PropTypes.bool.isRequired,
    calcShortest: PropTypes.object.isRequired,
    calcLongest: PropTypes.object.isRequired,
    calcActualWithTerm: PropTypes.object.isRequired,
    handleRequestOverride: PropTypes.func.isRequired,
    updateCreditApplication: PropTypes.func.isRequired,
    overrideInProgress: PropTypes.bool.isRequired,
    toggleOverrideInProgress: PropTypes.func.isRequired
  }

  state = {
    chosenTerm: this.props.overrideStatus !== null
      ? this.props.overrideTerm
      : this.props.calcTerm,
  }

  handleChosenTermChange = (e, override, toggleOverrideInProgress) => {
    toggleOverrideInProgress()

    const chosenTerm = parseFloat(e.target.value)

    let input = {}

    if (override) {
      input.overrideTerm = chosenTerm
    } else {
      input.calcTerm = chosenTerm
    }

    this.setState(
      { chosenTerm },
      () => {
        this.props.updateCreditApplication(input)
          .then(toggleOverrideInProgress)
          .catch(error => Sentry.captureMessage(`Calculation term ${override ? 'override' : 'update'} failed - ${error}`))
      }
    )
  }

  renderComparisonTableRows = (shortest, longest, actual, override, overrideCompleted, overrideRequested, locked, overrideInProgress) => (
    Object.keys(shortest).map((key, index) => {
      if (index === 0) {
        return (
          <tr key={`comparisonTable_${key}`}>
            <th className='slds-size_1-of-2 slds-theme_shade slds-text-heading_label'>
              {key.replace(/\d. /g, '')}  {/* removes number part of key, i.e. '1. ', '2. ', etc */}
            </th>
            <td>{shortest[key]}</td>
            <td>{longest[key]}</td>
            <td style={{ backgroundColor: 'rgba(234, 250, 234, 0.7)' }}>
              <ReadOnlyContext.Consumer>
                {readOnly => (
                  <Select
                    id='chosenTerm'
                    value={this.state.chosenTerm}
                    options={override
                      ? createArrayWithSequence(this.props.creditMaxTerm)
                      : overrideCompleted
                        ? [this.state.chosenTerm]
                        : createArrayWithSequence(longest[key] - shortest[key] + 1, shortest[key])
                    }
                    disabled={locked || (override === false && (overrideCompleted || overrideRequested || readOnly))}
                    onChange={e => this.handleChosenTermChange(e, override, this.props.toggleOverrideInProgress)}
                    className='slds-float_right'
                  />
                )}
              </ReadOnlyContext.Consumer>
            </td>
          </tr>
        )
      } else {
        return (
          <tr key={`comparisonTable_${key}`}>
            <th className='slds-cell-wrap slds-theme_shade slds-text-heading_label' style={{ lineHeight: 1.2 }}>
              {key.replace(/\d. /g, '')}
            </th>
            <td>{shortest[key]}</td>
            <td>{longest[key]}</td>
            <td style={{ backgroundColor: 'rgba(234, 250, 234, 0.7)' }}>
              {overrideInProgress
                ? <Spinner size='x-small' className='slds-is-relative slds-float_right slds-m-right_medium' />
                : actual[key]
              }
            </td>
          </tr>
        )
      }
    })
  )

  render() {
    const { showOverride, overridePerformed, overrideShortest, overrideLongest, overrideActualWithTerm, overrideCompleted, overrideRequested, calcShortest, calcLongest, calcActualWithTerm, locked, handleRequestOverride, clientEligibleForLoan, overrideInProgress } = this.props
    return (
      <Fragment>
        {clientEligibleForLoan
          ? <Fragment>
              <div className='slds-m-top_small slds-m-bottom_x-small'>
                <SvgIcon
                  variant='utility'
                  symbol='info'
                  size='small'
                  colour='light'
                  className='slds-m-right_x-small'
                />
                <i className='slds-text-body_small'>
                  All the calculations in section below are approximate and will only be finalised with Mycomax entry
                </i>
              </div>
              {(showOverride && !locked) || overridePerformed
                ? <Table
                    columns={['', 'Shortest', 'Longest', 'Chosen']}
                    rows={this.renderComparisonTableRows(overrideShortest, overrideLongest, overrideActualWithTerm, showOverride, overrideCompleted, overrideRequested, false, overrideInProgress)}
                    objectRows={true}
                    className='slds-no-row-hover table-cells-align_right table-header-cells-align_right slds-m-bottom_medium'
                  />
                : <Table
                    columns={['', 'Shortest', 'Longest', 'Chosen']}
                    rows={this.renderComparisonTableRows(calcShortest, calcLongest, calcActualWithTerm, false, overrideCompleted, overrideRequested, locked, overrideInProgress)}
                    objectRows={true}
                    className='slds-no-row-hover table-cells-align_right table-header-cells-align_right slds-m-bottom_medium'
                  />
              }
              <div className='slds-float_right slds-m-top_medium slds-m-bottom_large'>
                {showOverride
                  ? null
                  : <Button
                      minWidth={175}
                      disabled={overrideRequested}
                      onClick={handleRequestOverride}>
                      {overrideRequested
                        ? 'Override requested'
                        : 'Request override'
                      }
                    </Button>
                }
              </div>
            </Fragment>
          : <Warning>
              Client is not currently eligible for requested loan. See calculation results for underlying reasons.
            </Warning>
        }
      </Fragment>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/creditapplication-modal/CalculationTermComparison.js